import React, { useRef } from 'react';
import { X, Printer, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Encomenda } from '../../../services/encomendaApi';
import { CondoType, formatUnitLabel } from '../../../utils/terminology';

interface EncomendaQrModalProps {
  open: boolean;
  encomenda: Encomenda | null;
  /** Decides Bloco/Unidade vs Quadra/Lote — a loteamento has no "Ap". */
  condoType?: CondoType;
  onClose: () => void;
}

export default function EncomendaQrModal({ open, encomenda, condoType, onClose }: EncomendaQrModalProps) {
  const printRef = useRef<HTMLDivElement>(null);

  if (!open || !encomenda) return null;

  const unitLabel = encomenda.unit
    ? formatUnitLabel({ number: encomenda.unit.number, blockName: encomenda.unit.block?.name }, condoType) || '—'
    : '—';
  const personLabel = encomenda.person
    ? `${encomenda.person.first_name} ${encomenda.person.last_name}`
    : null;

  const formatDate = (d: string) =>
    new Date(d).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });

  const qrValue = encomenda.id;

  const handlePrint = () => {
    const svgEl = printRef.current?.querySelector('svg');
    const svgHtml = svgEl?.outerHTML ?? '';

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Encomenda ${encomenda.id.slice(0, 8).toUpperCase()}</title>
  <style>
    body { font-family: monospace; margin: 0; padding: 24px; text-align: center; background: #fff; color: #111; }
    h2 { font-size: 16px; margin: 0 0 4px; }
    p { font-size: 12px; margin: 3px 0; }
    .id { font-size: 10px; color: #888; margin-bottom: 12px; }
    .qr { margin: 12px auto; display: block; }
    .divider { border-top: 1px dashed #ccc; margin: 12px 0; }
    .label { font-weight: bold; }
  </style>
</head>
<body>
  <h2>Encomenda</h2>
  <p class="id">#${encomenda.id.slice(0, 8).toUpperCase()}</p>
  <div class="qr">${svgHtml}</div>
  <div class="divider"></div>
  <p><span class="label">Unidade:</span> ${unitLabel}</p>
  ${personLabel ? `<p><span class="label">Destinatário:</span> ${personLabel}</p>` : ''}
  ${encomenda.transportadora ? `<p><span class="label">Transportadora:</span> ${encomenda.transportadora}</p>` : ''}
  ${encomenda.tracking_code ? `<p><span class="label">Rastreio:</span> ${encomenda.tracking_code}</p>` : ''}
  <p><span class="label">Chegou:</span> ${formatDate(encomenda.created_at)}</p>
</body>
</html>`;

    // Use iframe to avoid window.open issues in Electron
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) { document.body.removeChild(iframe); return; }
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-xs bg-gray-950 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <QrCode className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-semibold text-white">QR Code da Encomenda</span>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white rounded transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 flex flex-col items-center gap-4">
          {/* QR code */}
          <div ref={printRef} className="bg-white rounded-xl p-4 shadow-inner">
            <QRCodeSVG value={qrValue} size={200} level="M" includeMargin={false} />
          </div>

          {/* Package info */}
          <div className="text-center space-y-0.5">
            <p className="text-white font-semibold text-sm">{unitLabel}</p>
            {personLabel && <p className="text-gray-400 text-xs">{personLabel}</p>}
            {encomenda.transportadora && (
              <p className="text-gray-500 text-xs">{encomenda.transportadora}</p>
            )}
            {encomenda.tracking_code && (
              <p className="text-gray-600 text-xs font-mono">{encomenda.tracking_code}</p>
            )}
            <p className="text-gray-600 text-xs pt-1">Chegou: {formatDate(encomenda.created_at)}</p>
            <p className="text-gray-700 text-[10px] font-mono"># {encomenda.id.slice(0, 8).toUpperCase()}</p>
          </div>

          {/* Print button */}
          <button
            onClick={handlePrint}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/40 text-sm font-semibold transition-colors"
          >
            <Printer className="h-4 w-4" />
            Imprimir etiqueta
          </button>
        </div>
      </div>
    </div>
  );
}
